import { useEffect } from 'react'
import useDebouncedEffect from 'use-debounced-effect'
import { trackInternalEvent } from '@rio/tracking'
import { TextField } from 'builder/components/TextField'
import { FetchStatuses } from 'builder/modules/constants'
import { useI18n } from 'builder/hooks/useI18n'
import { ButtonTheme, ButtonV2 } from 'builder/components/Button'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import Icon24 from 'builder/components/Icon'
import {
  Title,
  SubTitle,
  ButtonContainer,
  TextFieldContainer,
  ValidationLoadingIcon,
  ValidationSuccessIcon,
  ValidationFailIcon,
  Container,
  Image,
  CloseIconWrapper,
  CloseIcon,
  Button,
  ContentContainer,
  HeaderWrapper,
  BackButton,
} from '../styles'
import { LOCALIZATION_KEY } from '../constants'
import noPhotoCompositionImage from '../assets/no-photo-composition.png'
import { useCreateResumeWithLinkedin } from '../useCreateResumeWithLinkedin'

const getStatusIcon = (status: FetchStatuses | null): JSX.Element => {
  switch (status) {
    case FetchStatuses.loading:
      return <ValidationLoadingIcon />
    case FetchStatuses.loaded:
      return <ValidationSuccessIcon />
    case FetchStatuses.failed:
      return <ValidationFailIcon />
    default:
      return <></>
  }
}

export const LinkedInPrefill = () => {
  const { i18n } = useI18n()
  const mediaQueries = useMediaQueries()
  const {
    linkedInProfileURL,
    fetchLinkedInProfileStatus,
    handleFetchLinkedInProfile,
    setLinkedInFetchError,
    linkedInFetchError,
    isLinkValid,
    handleChangeURL,
    linkValidationStatus,
    setLinkValidationStatus,
    handlePrefillBack,
    handleClose,
  } = useCreateResumeWithLinkedin()

  useDebouncedEffect(
    () => {
      let eventStatus = ''
      if (linkedInProfileURL) {
        if (isLinkValid) {
          setLinkValidationStatus(FetchStatuses.loaded)
          setLinkedInFetchError(null)
          eventStatus = 'ok'
        } else {
          setLinkValidationStatus(FetchStatuses.failed)
          setLinkedInFetchError(i18n.t(`${LOCALIZATION_KEY}.prefill_modal.input_error_invalid`))
          eventStatus = 'failed'
        }
        trackInternalEvent('provide_linkedin_link', {
          link: linkedInProfileURL,
          status: eventStatus,
          label: 'linkedin_prefill_screen_li_prefill',
        })
      }
    },
    1000,
    [linkedInProfileURL],
  )

  useEffect(() => {
    if (
      !linkedInProfileURL &&
      linkedInFetchError === i18n.t(`${LOCALIZATION_KEY}.prefill_modal.input_error_invalid`)
    ) {
      setLinkedInFetchError(null)
    }
  }, [linkedInProfileURL, linkedInFetchError, i18n, setLinkedInFetchError])

  useEffect(() => {
    trackInternalEvent('see_linkedin_prefill_screen', {
      label: 'li_prefill',
    })
  }, [])

  return (
    <Container>
      <CloseIconWrapper onClick={handleClose}>
        <CloseIcon />
      </CloseIconWrapper>
      {mediaQueries.isPhone && (
        <BackButton
          size="small"
          leadingIcon={<Icon24.ChevronLeftCurrentColor />}
          onClick={handlePrefillBack}
          isDisabled={fetchLinkedInProfileStatus === FetchStatuses.loading}
        >
          {i18n.t(`${LOCALIZATION_KEY}.prefill_modal.back_button_text`)}
        </BackButton>
      )}
      <ContentContainer>
        <Image src={noPhotoCompositionImage} alt="Create Resume With LinkedIn" />
        <HeaderWrapper>
          <Title>{i18n.t(`${LOCALIZATION_KEY}.prefill_modal.title`)}</Title>
          <SubTitle>{i18n.t(`${LOCALIZATION_KEY}.prefill_modal.description`)}</SubTitle>
        </HeaderWrapper>
        <TextFieldContainer>
          <TextField
            value={linkedInProfileURL}
            name="linkedInURL"
            placeholder={i18n.t(`${LOCALIZATION_KEY}.prefill_modal.input_placeholder`)}
            onChange={handleChangeURL}
            inputIcon={getStatusIcon(linkValidationStatus)}
            inputIconPosition="right"
            error={linkedInFetchError}
            disabled={fetchLinkedInProfileStatus === FetchStatuses.loading}
            data-testid="linkedin-prefill-input"
            errorDataTestId="linkedin-prefill-input-error"
          />
        </TextFieldContainer>
      </ContentContainer>
      <ButtonContainer>
        {!mediaQueries.isPhone && (
          <ButtonV2
            theme={ButtonTheme.ghost}
            onClick={handlePrefillBack}
            isDisabled={fetchLinkedInProfileStatus === FetchStatuses.loading}
          >
            {i18n.t(`${LOCALIZATION_KEY}.prefill_modal.back_button_text`)}
          </ButtonV2>
        )}
        <Button
          onClick={handleFetchLinkedInProfile}
          isLoading={fetchLinkedInProfileStatus === FetchStatuses.loading}
          disabled={linkValidationStatus === FetchStatuses.loading}
        >
          {i18n.t(`${LOCALIZATION_KEY}.prefill_modal.continue_button_text`)}
        </Button>
      </ButtonContainer>
    </Container>
  )
}
